import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import { useAuth } from '../hooks';
import '../styles/Authorities.css';

const CATEGORIES = [
  {
    id: "crypto",
    label: "Crypto & Digital Assets",
    frameworks: [
      {
        id: "mica", code: "MiCA", fullName: "Markets in Crypto-Assets Regulation",
        type: "CRYPTO", category: "Crypto & Digital Assets",
        status: "ACTIVE", date: "Dec 2024", heat: 94,
        description: "Comprehensive EU licensing framework for crypto-asset service providers, e-money token issuers, and asset-referenced token issuers. Full CASP transitional deadline July 2026.",
        authority: "ESMA / EBA / NCAs",
        enforcement: "30 Dec 2024",
        tags: ["CASP Licensing", "Stablecoins", "Whitepapers"],
        tracks: ["CASP", "Exchange", "Custody"],
      },
      {
        id: "tfr", code: "TFR", fullName: "Transfer of Funds Regulation (Travel Rule)",
        type: "CRYPTO", category: "Crypto & Digital Assets",
        status: "ACTIVE", date: "Dec 2024", heat: 71,
        description: "Requires CASPs to collect and transmit originator and beneficiary information for all crypto transfers, including unhosted wallets above €1,000.",
        authority: "EBA / NCAs",
        enforcement: "30 Dec 2024",
        tags: ["Travel Rule", "KYC", "CASP"],
        tracks: ["CASP", "Exchange"],
      },
      {
        id: "dlt", code: "DLT Pilot", fullName: "DLT Pilot Regime",
        type: "CRYPTO", category: "Crypto & Digital Assets",
        status: "TRANSITIONAL", date: "Until 2026", heat: 38,
        description: "Regulatory sandbox for market infrastructures using distributed ledger technology to trade and settle tokenised financial instruments. Extended to 2026.",
        authority: "ESMA / NCAs",
        enforcement: "Until 2026",
        tags: ["Tokenised Securities", "Sandbox"],
        tracks: ["Exchange"],
      },
    ],
  },
  {
    id: "aml",
    label: "AML / KYC / Counter-Terrorism Financing",
    frameworks: [
      {
        id: "amld56", code: "AMLD5/6", fullName: "5th & 6th Anti-Money Laundering Directives",
        type: "AML", category: "AML / KYC / CTF",
        status: "ACTIVE", date: "Active", heat: 76,
        description: "Foundation EU AML framework: enhanced customer due diligence, beneficial ownership transparency, and counter-terrorism financing obligations for obliged entities.",
        authority: "EBA / National FIUs",
        enforcement: "Active",
        tags: ["AML", "CTF", "Due Diligence"],
        tracks: ["CASP", "Bank", "Payment", "Exchange", "Custody"],
      },
      {
        id: "aml-reg", code: "AML Regulation", fullName: "EU AML Regulation (AMLR)",
        type: "AML", category: "AML / KYC / CTF",
        status: "ADOPTED", date: "Jun 2024 → Applies 2027", heat: 72,
        description: "Unified AML Regulation adopted June 2024. Directly applicable across all EU member states from 2027, replacing the Directive model to eliminate gold-plating and inconsistency.",
        authority: "AMLA / NCAs",
        enforcement: "2027",
        tags: ["Harmonisation", "Direct Regulation"],
        tracks: ["CASP", "Bank", "Payment", "Exchange", "Custody"],
      },
      {
        id: "amla", code: "AMLA", fullName: "Anti-Money Laundering Authority",
        type: "AML", category: "AML / KYC / CTF",
        status: "ACTIVE", date: "2025 → Full powers 2027", heat: 68,
        description: "EU's new central AML supervisor, headquartered in Frankfurt. Directly supervises high-risk obliged entities from 2027. AMLA operational as authority since 2025.",
        authority: "AMLA",
        enforcement: "2025",
        tags: ["Central Authority", "Supervision", "Frankfurt"],
        tracks: ["CASP", "Bank"],
      },
      {
        id: "fatf", code: "FATF", fullName: "FATF Recommendations",
        type: "AML", category: "AML / KYC / CTF",
        status: "INTERNATIONAL", date: "Ongoing", heat: 55,
        description: "International standards for combating money laundering and terrorist financing. EU AML regulations are designed to align with and implement FATF recommendations.",
        authority: "FATF",
        enforcement: "Ongoing",
        tags: ["Global Standards", "Best Practices"],
        tracks: ["CASP", "Bank", "Payment"],
      },
    ],
  },
  {
    id: "data",
    label: "Data, Cloud & Cybersecurity",
    frameworks: [
      {
        id: "dora", code: "DORA", fullName: "Digital Operational Resilience Act",
        type: "CYBERSECURITY", category: "Data, Cloud & Cybersecurity",
        status: "ACTIVE", date: "Jan 2025", heat: 81,
        description: "ICT risk management, incident reporting, third-party provider oversight, and resilience testing for all EU financial entities. EBA critical 3rd-party oversight begins April 2026.",
        authority: "ESMA / EBA / EIOPA / NCAs",
        enforcement: "17 Jan 2025",
        tags: ["ICT Risk", "Resilience", "Incident Response"],
        tracks: ["CASP", "Bank", "Insurer", "Payment", "Exchange"],
      },
      {
        id: "nis2", code: "NIS2", fullName: "NIS2 Directive",
        type: "CYBERSECURITY", category: "Data, Cloud & Cybersecurity",
        status: "ACTIVE", date: "Oct 2024", heat: 52,
        description: "Cybersecurity obligations for essential and important entities. Incident reporting, supply chain security, and management board accountability. Overlaps with DORA for financial sector.",
        authority: "ENISA / National NCAs",
        enforcement: "17 Oct 2024",
        tags: ["Cybersecurity", "Critical Infrastructure"],
        tracks: ["CASP", "Bank", "Payment", "Exchange"],
      },
      {
        id: "gdpr", code: "GDPR", fullName: "General Data Protection Regulation",
        type: "DATA PROTECTION", category: "Data, Cloud & Cybersecurity",
        status: "ACTIVE", date: "May 2018", heat: 47,
        description: "Comprehensive EU data protection law governing processing, controller obligations, cross-border transfers, and enforcement. 28+ enforcement actions against financial entities in 2024/25.",
        authority: "EDPB / National DPAs",
        enforcement: "25 May 2018",
        tags: ["Data Privacy", "Consent", "Rights"],
        tracks: ["CASP", "Bank", "Payment", "Exchange", "Custody"],
      },
      {
        id: "aiact", code: "AI Act", fullName: "EU Artificial Intelligence Act",
        type: "AI REGULATION", category: "Data, Cloud & Cybersecurity",
        status: "ACTIVE", date: "Phased 2024–2027", heat: 44,
        description: "Risk-based AI regulation. Prohibited AI: Aug 2024. GPAI obligations: Aug 2025. High-risk AI (financial credit scoring, biometrics): Feb 2026. Full enforcement: Aug 2027.",
        authority: "EU AI Office / NCAs",
        enforcement: "Phased 2024-2027",
        tags: ["AI Governance", "Risk Assessment", "Transparency"],
        tracks: ["Bank", "CASP", "Insurer"],
      },
      {
        id: "dataact", code: "Data Act", fullName: "EU Data Act",
        type: "DATA SHARING", category: "Data, Cloud & Cybersecurity",
        status: "ACTIVE", date: "Sep 2025", heat: 32,
        description: "Data sharing, access, and interoperability obligations across sectors. Governs B2B, B2G data access. Relevant to cloud service switching and financial data portability.",
        authority: "European Commission / NCAs",
        enforcement: "Sep 2025",
        tags: ["Data Sharing", "Interoperability"],
        tracks: ["Bank", "Payment"],
      },
    ],
  },
  {
    id: "identity",
    label: "Digital Identity & Trust",
    frameworks: [
      {
        id: "eidas2", code: "eIDAS 2.0", fullName: "European Digital Identity Regulation",
        type: "DIGITAL IDENTITY", category: "Digital Identity & Trust",
        status: "ACTIVE", date: "2025–2026", heat: 48,
        description: "EU Digital ID Wallet for citizens and firms. Single digital identity usable across all EU member states and sectors. Member states must accept EUDI wallet from 2026.",
        authority: "European Commission / NCAs",
        enforcement: "2025-2026",
        tags: ["Digital ID", "EUDI Wallet", "Cross-border"],
        tracks: ["CASP", "Bank", "Payment"],
      },
      {
        id: "eudi", code: "EUDI Wallet", fullName: "EU Digital Identity Wallet Framework",
        type: "DIGITAL IDENTITY", category: "Digital Identity & Trust",
        status: "TESTING", date: "Pilots active 2025", heat: 35,
        description: "Verifiable credentials and cross-border digital identity infrastructure. Large-scale pilot programmes (LSPs) running across EU member states. Financial sector integration scope.",
        authority: "European Commission",
        enforcement: "Pilots 2025",
        tags: ["Credentials", "Pilots"],
        tracks: ["Bank", "CASP"],
      },
      {
        id: "esig", code: "eSignature", fullName: "Electronic Signature Regulation (eIDAS)",
        type: "DIGITAL IDENTITY", category: "Digital Identity & Trust",
        status: "ACTIVE", date: "Jul 2016", heat: 22,
        description: "Legal framework for e-signatures and trust services. Ensures legal recognition of electronic transactions across EU. eIDAS 2.0 updates this framework for the digital wallet era.",
        authority: "ENISA / NCAs",
        enforcement: "Jul 2016",
        tags: ["e-Signature", "Legal Validity"],
        tracks: ["Bank", "Payment"],
      },
    ],
  },
  {
    id: "payments",
    label: "Financial Services & Payments",
    frameworks: [
      {
        id: "psd3", code: "PSD3 / PSR", fullName: "Payment Services Directive 3 & Regulation",
        type: "PAYMENTS", category: "Financial Services & Payments",
        status: "PLANNED", date: "Adopted ~2026 · Applies 2027", heat: 58,
        description: "Modernised payment services framework. PSR directly applicable across EU. PSD3 harmonises national transposition. Trilogue concluded — Commission adoption expected H1 2026.",
        authority: "EBA / European Commission",
        enforcement: "Expected 2026-2027",
        tags: ["Open Banking", "APIs", "SCA"],
        tracks: ["Payment", "Bank", "E-Money"],
      },
      {
        id: "emd3", code: "EMD3", fullName: "E-Money Directive 3 (via PSD3/PSR)",
        type: "PAYMENTS", category: "Financial Services & Payments",
        status: "PLANNED", date: "Absorbed into PSD3", heat: 42,
        description: "EMD2 successor substantially absorbed into PSD3/PSR legislative package. Separate EMD3 may not proceed — EMI regime unified under PSD3. Monitor Commission final text.",
        authority: "EBA / NCAs",
        enforcement: "Via PSD3",
        tags: ["EMI Licensing", "Digital Money"],
        tracks: ["E-Money", "Payment"],
      },
      {
        id: "sepa", code: "SEPA / IPR", fullName: "SEPA & Instant Payments Regulation",
        type: "PAYMENTS", category: "Financial Services & Payments",
        status: "ACTIVE", date: "Active / IPR Jan 2025", heat: 36,
        description: "Instant Payments Regulation mandates PSPs to offer euro instant credit transfers. Verification of Payee obligation for all EU PSPs. Full compliance deadline Oct 2025.",
        authority: "ECB / EPC / NCAs",
        enforcement: "Jan 2025",
        tags: ["Instant Payments", "Eurozone", "Real-time"],
        tracks: ["Payment", "Bank"],
      },
      {
        id: "fida", code: "FIDA", fullName: "Financial Data Access Regulation",
        type: "PAYMENTS", category: "Financial Services & Payments",
        status: "PLANNED", date: "2025–2026", heat: 31,
        description: "Open finance framework extending beyond payment accounts to investments, insurance, pensions, and mortgages. Trilogue expected completion end 2026.",
        authority: "European Commission / EBA",
        enforcement: "2025-2026",
        tags: ["Open Finance", "Data Sharing"],
        tracks: ["Bank", "Payment", "E-Money"],
      },
    ],
  },
  {
    id: "esg",
    label: "ESG, Sustainability & Corporate Governance",
    frameworks: [
      {
        id: "csrd", code: "CSRD", fullName: "Corporate Sustainability Reporting Directive",
        type: "ESG", category: "ESG, Sustainability & Corporate Governance",
        status: "ACTIVE", date: "Jan 2025 (large cos)", heat: 51,
        description: "Mandatory sustainability reporting aligned to European Sustainability Reporting Standards (ESRS). Large listed companies from Jan 2025; SMEs phased to 2026.",
        authority: "EFRAG / NCAs",
        enforcement: "Jan 2025",
        tags: ["ESG Reporting", "ESRS", "Disclosure"],
        tracks: ["Bank", "Insurer"],
      },
      {
        id: "sfdr", code: "SFDR", fullName: "Sustainable Finance Disclosure Regulation",
        type: "ESG", category: "ESG, Sustainability & Corporate Governance",
        status: "ACTIVE", date: "Mar 2021", heat: 43,
        description: "Sustainability disclosure for financial products. Article 6, 8, 9 classification framework. SFDR review underway — Commission considering simplified product categories.",
        authority: "ESMA / NCAs",
        enforcement: "Mar 2021",
        tags: ["Green Finance", "ESG Funds", "Transparency"],
        tracks: ["Bank", "Insurer"],
      },
      {
        id: "taxonomy", code: "EU Taxonomy", fullName: "EU Taxonomy Regulation",
        type: "ESG", category: "ESG, Sustainability & Corporate Governance",
        status: "ACTIVE", date: "Active", heat: 38,
        description: "Defines environmentally sustainable economic activities using science-based criteria. Six environmental objectives. Financial institutions must disclose Taxonomy alignment.",
        authority: "European Commission / ESMA",
        enforcement: "Active",
        tags: ["Green Definition", "Climate"],
        tracks: ["Bank", "Insurer"],
      },
      {
        id: "csddd", code: "CSDDD", fullName: "Corporate Sustainability Due Diligence Directive",
        type: "ESG", category: "ESG, Sustainability & Corporate Governance",
        status: "ACTIVE", date: "2026 → phased", heat: 34,
        description: "Human rights and environmental due diligence across corporate value chains. Corporate accountability for supply chain impacts. Financial sector scope phased.",
        authority: "European Commission / NCAs",
        enforcement: "2026 phased",
        tags: ["Supply Chain", "Human Rights"],
        tracks: ["Bank"],
      },
    ],
  },
  {
    id: "markets",
    label: "Markets, Trading & Investments",
    frameworks: [
      {
        id: "mifid2", code: "MiFID II", fullName: "Markets in Financial Instruments Directive II",
        type: "MARKETS", category: "Markets, Trading & Investments",
        status: "ACTIVE", date: "Jan 2018", heat: 38,
        description: "Investor protection, best execution, product governance, and reporting for investment firms. MiFIR review RTS adoption expected June 2026. Equity transparency rules updated.",
        authority: "ESMA / NCAs",
        enforcement: "Jan 2018",
        tags: ["Investor Protection", "Transparency", "Trading"],
        tracks: ["Exchange", "Bank"],
      },
      {
        id: "aifmd2", code: "AIFMD II", fullName: "Alternative Investment Fund Managers Directive II",
        type: "MARKETS", category: "Markets, Trading & Investments",
        status: "ACTIVE", date: "Apr 2024", heat: 36,
        description: "Enhanced regulation for AIFMs managing private equity, VC, and hedge funds. Loan origination fund regime, liquidity management, and delegation enhancements.",
        authority: "ESMA / NCAs",
        enforcement: "Apr 2024",
        tags: ["Private Equity", "VC", "Hedge Funds"],
        tracks: ["Bank"],
      },
      {
        id: "emir", code: "EMIR Refit", fullName: "EMIR / EMIR Refit",
        type: "MARKETS", category: "Markets, Trading & Investments",
        status: "ACTIVE", date: "Updated 2024", heat: 33,
        description: "Derivatives clearing and trade reporting. EMIR Refit updated 2024 to improve efficiency and reduce compliance costs. New ESMA validation rules for trade repositories.",
        authority: "ESMA / NCAs",
        enforcement: "Updated 2024",
        tags: ["Derivatives", "Clearing", "Reporting"],
        tracks: ["Exchange", "Bank"],
      },
      {
        id: "ucits", code: "UCITS VI", fullName: "UCITS Directive",
        type: "MARKETS", category: "Markets, Trading & Investments",
        status: "ACTIVE", date: "Under review", heat: 28,
        description: "Harmonised regulatory framework for UCITS mutual funds — investor protection and cross-border distribution. UCITS VI review expected in parallel with AIFMD II.",
        authority: "ESMA / NCAs",
        enforcement: "Under review",
        tags: ["Mutual Funds", "UCITS"],
        tracks: ["Bank"],
      },
    ],
  },
];

const ALL_CATEGORIES = ["All"].concat(CATEGORIES.map(c => c.label));
const TRACKS = ["All", "CASP", "Exchange", "Bank", "Payment", "Custody", "E-Money", "Insurer"];

const RegulatoryTaxonomy = () => {
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTrack, setSelectedTrack] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const allFrameworks = CATEGORIES.flatMap(cat => cat.frameworks);

  const filteredFrameworks = allFrameworks.filter(framework => {
    const matchesCategory = selectedCategory === 'All' || framework.category === selectedCategory;
    const matchesTrack = selectedTrack === 'All' || framework.tracks.includes(selectedTrack);
    const matchesSearch = searchQuery === '' ||
      framework.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      framework.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      framework.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesTrack && matchesSearch;
  });

  // Group filtered frameworks by category
  const groupedFrameworks = CATEGORIES.map(cat => ({
    ...cat,
    frameworks: filteredFrameworks.filter(f => f.category === cat.label)
  })).filter(cat => cat.frameworks.length > 0);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedTrack('All');
  };

  const heatColor = (score) => {
    if (score >= 85) return { label: "CRITICAL", color: "#DC2626" };
    if (score >= 65) return { label: "HIGH", color: "#EA580C" };
    if (score >= 45) return { label: "MEDIUM", color: "#EAB308" };
    return { label: "STABLE", color: "#22C55E" };
  };

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />
      <div className="app">
        <Sidebar />
        <div className="main-content">
          <div className="content-area">

            {/* Hero Section */}
            <div className="authorities-hero">
              <div className="authorities-hero-content">
                <div className="authorities-hero-label">EU REGULATORY FRAMEWORK TAXONOMY</div>
                <h1 className="authorities-hero-title">Complete EU Regulatory Coverage</h1>
                <p className="authorities-hero-description">
                  Browse {allFrameworks.length} regulatory frameworks across 7 domains. Filter by compliance track (CASP, Exchange, Bank, Payment, Custody) to view applicable regulations.
                </p>
              </div>
            </div>

            {/* Search Section */}
            <div className="authorities-search-section">
              <form onSubmit={handleSearch} className="authorities-search-form">
                <div className="search-input-wrapper">
                  <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    className="authorities-search-input"
                    placeholder="Search frameworks by name, code, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="clear-button"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                  <button type="submit" className="search-button">
                    Search
                  </button>
                </div>
              </form>

              {/* Filters */}
              <div className="authorities-filters">
                <div className="filter-group">
                  <label className="filter-label">Category</label>
                  <select
                    className="filter-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {ALL_CATEGORIES.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Compliance Track</label>
                  <select
                    className="filter-select"
                    value={selectedTrack}
                    onChange={(e) => setSelectedTrack(e.target.value)}
                  >
                    {TRACKS.map(track => (
                      <option key={track} value={track}>{track}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-stats">
                  <span className="stats-count">{filteredFrameworks.length}</span>
                  <span className="stats-label">frameworks found</span>
                </div>
              </div>
            </div>

            {/* Frameworks by Category */}
            <div className="authorities-section">
              {filteredFrameworks.length === 0 ? (
                <div className="empty-state">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="empty-icon">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                  </svg>
                  <p>No frameworks found matching your search criteria.</p>
                  <p className="empty-hint">Try adjusting your filters or search query.</p>
                  <button onClick={handleClearSearch} className="reset-filters-btn">
                    Clear Filters
                  </button>
                </div>
              ) : (
                <>
                  {groupedFrameworks.map(category => (
                    <div key={category.id} style={{
                      marginBottom: '48px',
                      paddingTop: '24px',
                      borderTop: '1px solid #E1DFDD'
                    }}>
                      <h2 style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#242424',
                        marginBottom: '20px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        fontFamily: "'Consolas', 'Monaco', monospace"
                      }}>
                        {category.label}
                      </h2>
                      <div className="authorities-grid">
                        {category.frameworks.map(framework => {
                          const heat = heatColor(framework.heat);
                          return (
                            <div key={framework.id} className="authority-card">
                              <div className="authority-header">
                                <div className="authority-type-badge">
                                  {framework.type}
                                </div>
                                <div className="authority-code">{framework.code}</div>
                              </div>

                              <h3 className="authority-name">{framework.fullName}</h3>

                              <p className="authority-description">{framework.description}</p>

                              <div className="authority-meta">
                                <div className="meta-item">
                                  <span className="meta-label">Status:</span>
                                  <span className="meta-value">{framework.status}</span>
                                </div>
                                <div className="meta-item">
                                  <span className="meta-label">Enforcement:</span>
                                  <span className="meta-value">{framework.enforcement}</span>
                                </div>
                                <div className="meta-item">
                                  <span className="meta-label">Heat Score:</span>
                                  <span className="meta-value" style={{ color: heat.color, fontWeight: 600 }}>
                                    {framework.heat} - {heat.label}
                                  </span>
                                </div>
                              </div>

                              <div className="authority-responsibilities">
                                <h4>Compliance Tracks</h4>
                                <ul>
                                  {framework.tracks.map((track, idx) => (
                                    <li key={idx}>{track}</li>
                                  ))}
                                </ul>
                              </div>

                              <div className="authority-frameworks">
                                <span className="frameworks-label">TAGS</span>
                                <div className="frameworks-tags">
                                  {framework.tags.map(tag => (
                                    <span key={tag} className="framework-tag">{tag}</span>
                                  ))}
                                </div>
                              </div>

                              <div className="authority-footer">
                                <span className="authority-link" style={{ color: '#605E5C' }}>
                                  Authority: {framework.authority}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default RegulatoryTaxonomy;
